import SquireCheckbox from "components/Checkbox/SquireCheckobx";
import React, { useState, useEffect } from "react";
import { ReactComponent as PlanIcon } from "assets/icons/plan_icon.svg";
import useSubscription from "hooks/useSubscription";
import ButtonSpinner from "components/Loading/ButtonSpinner";
import useAuth from "hooks/useAuth";
import useDialog from "hooks/useDialog";
import { PLAN_FEATURES } from "utils/constants";

const Prices = () => {
  const { subscription, subscribe, loading, subscriptionStatus } =
    useSubscription();
  const { user } = useAuth();
  const { openSigninDialog } = useDialog();
  const [isQuarterly, setIsQuarterly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("free");

  // Set initial state of toggle based on current subscription
  useEffect(() => {
    if (subscriptionStatus.hasPremium) {
      setSelectedPlan("premium");
      if (subscriptionStatus.isQuarterly) {
        setIsQuarterly(true);
      }
    }
  }, [subscription]);

  const handleToggleQuarterly = (e) => {
    setIsQuarterly(e.target.checked);
  };

  const handleSubscribe = (planType) => {
    if (!user) {
      // If not logged in, open signup dialog first
      openSigninDialog();
      return;
    }

    let planId;
    if (planType === "premium") {
      // Set the correct plan ID based on billing frequency
      planId = isQuarterly
        ? process.env.REACT_APP_STRIPE_PRICE_PREMIUM_QUARTERLY
        : process.env.REACT_APP_STRIPE_PRICE_PREMIUM;
    }

    if (planId) {
      subscribe(
        planId,
        `${window.location.origin}/settings?tab=subscription&status=success`,
        `${window.location.origin}?status=cancel`,
        isQuarterly
      );
    }
  };

  return (
    <>
      {loading.isFetchingSubscription ? (
        <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="animate-spin rounded-full h-20 w-20 border-b-[3px] border-gray-300"></div>
        </div>
      ) : (
        <div className="bg-white h-auto rounded-[10px] p-3 pb-[44px] sm:py-[30px] sm:px-[28px]">
          <h1 className="text-[18px] sm:text-[24px] font-bold text-[#000000CC] leading-[100%]">
            Piano Attuale:
          </h1>
          <div
            onClick={() => setSelectedPlan("free")}
            className={`border-2 mt-[21px] rounded-[12px] ${
              selectedPlan === "free" || subscriptionStatus.hasActive
                ? "border-primary"
                : "border-[#F0F0F0]"
            }`}
          >
            <div
              className={`flex items-center justify-between h-[56px] pl-4 pr-5 ${
                selectedPlan === "free" || subscriptionStatus.hasActive
                  ? "border-b-2 border-primary"
                  : "border-b-2 border-[#F0F0F0]"
              }`}
            >
              <div className="flex items-center space-x-3 ">
                <div
                  className={`w-[32px] h-[32px] rounded-full ${
                    selectedPlan === "free" || subscriptionStatus.hasActive
                      ? "bg-[#1DA1F21F]"
                      : "bg-[#B2B2B21F]"
                  } flex items-center justify-center`}
                >
                  <PlanIcon
                    stroke={
                      selectedPlan === "free" || subscriptionStatus.hasActive
                        ? "#1DA1F2"
                        : "#252424"
                    }
                  />
                </div>
                <p className="text-[16px] font-semibold leading-[24px] text-[#344054]">
                  {subscriptionStatus.hasPremium
                    ? `Pro ${
                        subscriptionStatus.isQuarterly
                          ? "(Semestrale)"
                          : "(Mensile)"
                      }`
                    : "Piano Gratuito"}
                </p>
              </div>
              <SquireCheckbox
                checked={
                  selectedPlan === "free" || subscriptionStatus.hasActive
                }
                onChange={() => setSelectedPlan("free")}
                isCalculator={true}
                checkboxStyle={{ width: 20, height: 20 }}
              />
            </div>
            <div className="p-4">
              <h3 className="text-[30px] leading-[38px] font-semibold text-[#344054]">
                {subscriptionStatus.hasPremium
                  ? subscriptionStatus.isQuarterly
                    ? "€9,90 al mese"
                    : "€19,90 al mese"
                  : "Gratis"}
              </h3>
              <p className="text-[14px] font-normal leading-[20px] text-[#475467] mt-1">
                {subscriptionStatus.hasPremium
                  ? "Include: " + PLAN_FEATURES.premium.join(", ")
                  : "Include: " + PLAN_FEATURES.free.join(", ")}
              </p>
            </div>
          </div>
          {(!subscriptionStatus.hasActive ||
            !subscriptionStatus.hasPremium) && (
            <>
              <div className="mt-8">
                <h2 className="text-[18px] sm:text-[24px] font-bold leading-[100%] text-[#000000CC]">
                  Other Plans
                </h2>
              </div>
              <div
                onClick={() => setSelectedPlan("premium")}
                className={`border-2 mt-[21px] rounded-[12px] ${
                  selectedPlan === "premium"
                    ? "border-primary"
                    : "border-[#F0F0F0]"
                }`}
              >
                <div
                  className={`flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:items-center justify-center sm:justify-between h-[88px] sm:h-[56px] pl-4 pr-5 ${
                    selectedPlan === "premium"
                      ? "border-b-2 border-primary"
                      : "border-b-2 border-[#F0F0F0]"
                  }`}
                >
                  <div className="flex items-center space-x-3 ">
                    <div
                      className={`w-[32px] h-[32px] rounded-full ${
                        selectedPlan === "premium"
                          ? "bg-[#1DA1F21F]"
                          : "bg-[#B2B2B21F]"
                      } flex items-center justify-center`}
                    >
                      <PlanIcon
                        stroke={
                          selectedPlan === "premium" ? "#1DA1F2" : "#252424"
                        }
                      />
                    </div>
                    <p className="text-[16px] font-semibold leading-[24px] text-[#344054]">
                      Pro
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <h2 className="text-[14px] leading-[28px] text-[#727070] font-normal">
                      Mensile
                    </h2>
                    <div>
                      <input
                        checked={isQuarterly}
                        onChange={handleToggleQuarterly}
                        type="checkbox"
                        name="pricing_switch"
                        id="pricing_switch"
                        className="checkbox_input hidden"
                      />
                      <label
                        className="checkbox_label !bg-[#0011FF] !w-[35px] after:!top-[2px] after:w-[15px] after:h-[15px] cursor-pointer"
                        htmlFor="pricing_switch"
                      ></label>
                    </div>
                    <h2 className="text-[14px] leading-[28px] text-[#182230] font-normal line-clamp-1">
                      Semestrale (Risparmia il 50%)
                    </h2>
                    <SquireCheckbox
                      onChange={() => setSelectedPlan("premium")}
                      checked={selectedPlan === "premium"}
                      isCalculator={true}
                      checkboxStyle={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-[30px] leading-[38px] font-semibold text-[#344054]">
                    {isQuarterly ? "€9,90" : "€19,90"}{" "}
                    <sub className="text-[14px] -bottom-[0px] font-normal leading-[20px] text-[#475467]">
                      al mese
                    </sub>
                  </h3>
                  <p className="text-[14px] font-normal leading-[20px] text-[#475467] mt-1">
                    Include: {PLAN_FEATURES.premium.join(", ")}
                  </p>

                  {selectedPlan === "premium" && (
                    <>
                      <button
                        className="mt-4 bg-primary text-white w-full py-2 px-4 rounded-md font-medium"
                        onClick={() => handleSubscribe("premium")}
                        disabled={loading.isCreatingSubscription}
                      >
                        {loading.isCreatingSubscription ? (
                          <ButtonSpinner
                            style={{
                              borderWidth: "3px",
                              width: "19px",
                              height: "19px",
                            }}
                          />
                        ) : (
                          "Passa a Pro"
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Prices;
